import { createContext, useCallback, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { getStudioCreator, saveStudioCreator } from "../../data/client";
import type { Creator } from "../../data/types";
import { ErrorState, LoadingState } from "../states";

/**
 * Studio's own copy of the creator record.
 *
 * The patron context reads `/creator`, which has the grant matrix stripped.
 * Studio needs the matrix - it is the thing being edited - so it reads
 * `/studio/creator` instead. Two sources on purpose: the patron surface should
 * not be able to see the shape of the paywall even by accident.
 *
 * ## The save model - D-31
 *
 * **Nothing on a Studio screen writes on its own.** Every edit changes local
 * state and marks the record dirty; one SAVE commits it. That includes the
 * access matrix and the retire buttons, which are single clicks and could
 * defensibly have committed immediately - they stage like everything else,
 * because a screen where some controls save themselves and others wait is a
 * screen you have to have memorised.
 *
 * This replaces a write-per-change model that made typing impossible: each
 * keystroke PUT the whole record and then applied the reply with
 * `setCreator(saved)`, so a response landing mid-word overwrote the letters
 * typed after the request left.
 *
 * A successful response is still not applied. The server merges the body over
 * the stored record and returns it, so after a success it already matches
 * local, and applying it could only ever undo an edit made while the request
 * was in the air. A failure leaves local state alone: the edits are the user's
 * work, and the screen says the save failed rather than silently deleting it.
 */

type StudioState = {
  creator: Creator;
  /** Edit locally. Writes nothing - `save` does that. */
  update: (next: Creator) => void;
  /**
   * Replace local state and count it as already saved - for a value the server
   * has stored itself, like the result of a tier sync.
   */
  replace: (next: Creator) => void;
  /** Commit the current record. No-op when there is nothing to save. */
  save: () => Promise<void>;
  /** Put the record back as it was last saved. Sends nothing (FR-17). */
  discard: () => void;
  saving: boolean;
  /** Edits exist that no successful save has covered. */
  dirty: boolean;
  /** Set when a save fails; cleared by the next success. */
  error: string | null;
  /** When the last successful save landed, for the confirmation line (FR-25). */
  savedAt: Date | null;
};

const Context = createContext<StudioState | null>(null);

export function StudioProvider({ children }: { children: ReactNode }) {
  const [creator, setCreator] = useState<Creator | null>(null);
  const [loadError, setLoadError] = useState<Error | null>(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  /*
   * The record as the server last agreed it. Kept so DISCARD can restore it
   * without a request (FR-17, AC-8) - re-fetching would work but would turn
   * "undo my edits" into an operation that can fail, and a failed undo leaves
   * the author in a state neither they nor the server chose.
   */
  const [saved, setSaved] = useState<Creator | null>(null);

  useEffect(() => {
    let live = true;
    setLoadError(null);
    getStudioCreator()
      .then((c) => {
        if (!live) return;
        setCreator(c);
        setSaved(c);
      })
      .catch((e: Error) => live && setLoadError(e));
    return () => {
      live = false;
    };
  }, [reloadKey]);

  const update = useCallback((next: Creator) => {
    setCreator(next);
    setDirty(true);
  }, []);

  const replace = useCallback((next: Creator) => {
    setCreator(next);
    // Already stored server-side, so it is also the new baseline for DISCARD.
    setSaved(next);
    setDirty(false);
    setSavedAt(new Date());
  }, []);

  const discard = useCallback(() => {
    if (!saved) return;
    setCreator(saved);
    setDirty(false);
    setError(null);
  }, [saved]);

  const save = useCallback(async () => {
    if (!creator || saving || !dirty) return;
    setSaving(true);
    setError(null);
    try {
      await saveStudioCreator(creator);
      // Deliberately not applied - see the note above. It does become the new
      // baseline, though: this is now what the server holds.
      setSaved(creator);
      setDirty(false);
      setSavedAt(new Date());
    } catch (e) {
      // The edits stay. They are the user's work, and the record on screen is
      // still what they meant to save.
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }, [creator, dirty, saving]);

  /*
   * No `beforeunload` — FR-15, reversing part of D-31.
   *
   * The browser dialog names no record, offers no third option and cannot say
   * what would be lost. `GuardBar` does all three for the navigations Studio
   * owns. What the guard cannot intercept is browser back and tab close (W-7);
   * accepting that gap is deliberate, because the alternative was migrating
   * every route in the app to a data router to reach `useBlocker`.
   */

  if (loadError) {
    return (
      <ErrorState
        headline="STUDIO DID NOT LOAD"
        detail="The creator profile did not come back. Reloading usually settles it."
        code={`STUDIO_LOAD_FAILED · ${loadError.name.toUpperCase()}`}
        onRetry={() => setReloadKey((k) => k + 1)}
      />
    );
  }
  if (!creator) return <LoadingState label="OPENING STUDIO" subLabel="LOADING YOUR PROFILE" />;

  return (
    <Context.Provider
      value={{ creator, update, replace, save, discard, saving, dirty, error, savedAt }}
    >
      {children}
    </Context.Provider>
  );
}

export function useStudio(): StudioState {
  const value = useContext(Context);
  if (!value) throw new Error("useStudio must be used inside <StudioProvider>");
  return value;
}
